import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const SECONDARY_CATEGORY_LABEL_BY_API: Record<string, string> = {
  RENT: "Rent",
  COMMERCIAL_RENTED: "Commercial Rented",
  COMMERCIAL_OWNED: "Commercial Owned",
  RESIDENTIAL_OWNED: "Residential Owned",
  RESIDENTIAL_RENTED: "Residential Rented",
  GOLD_BARS_AND_COINS: "Gold Bars and Coins",
  SILVER_BARS_AND_COINS: "Silver Bars and Coins",
  JEWELLERY: "Jewellery",
  ARTS: "Arts",
  ANTIQUES: "Antiques",
  "GEMS_AND_DIAMONDS": "Gems & Diamonds",
  SPORTS_MEMORABLES: "Sports Memorables",
  COLLECTABLES: "Collectables",
  "WINE_AND_SPIRITS": "Wine & Spirits",
  CLASSIC_CARS: "Classic Cars",
  LUXURY_HANDBAGS: "Luxury Handbags",
  LUXURY_WATCHES: "Luxury Watches",
  OTHERS: "Others",
  PPF: "PPF",
  KVP: "KVP",
  NSC: "NSC",
  EPF: "EPF",
  INVIT: "InvIT",
  REIT: "REIT",
  OTHER: "Other",
};

const normalizeCategoryApiKey = (raw: string): string =>
  raw.trim().replace(/\s+/g, "_").toUpperCase();

const humanizeSecondaryCategory = (raw: string): string =>
  raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

export const mapSecondaryCategoryToLabel = (raw: unknown): string => {
  if (raw == null || raw === "" || raw === "-") return "-";
  const str = String(raw).trim();
  const apiKey = normalizeCategoryApiKey(str);
  if (SECONDARY_CATEGORY_LABEL_BY_API[apiKey]) {
    return SECONDARY_CATEGORY_LABEL_BY_API[apiKey];
  }
  const match = Object.values(SECONDARY_CATEGORY_LABEL_BY_API).find(
    (label) => label.toLowerCase() === str.toLowerCase(),
  );
  return match || humanizeSecondaryCategory(str);
};

export const normalizePrimaryCategoryLabel = (raw: string): string => {
  const normalized = String(raw || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const upper = normalized.toUpperCase();
  const aliases: Record<string, string> = {
    "REAL ESTATE": "Real Estate",
    "PRECIOUS METALS": "Precious Metals",
    "CUSTOM ASSET": "Custom Asset",
    "OTHER FINANCIAL": "Other Financial",
  };
  if (aliases[upper]) return aliases[upper];
  return normalized || "-";
};

const pickOtherAssetTxnMetadata = (
  record: Record<string, any> | null | undefined,
): Record<string, any> => {
  if (!record) return {};
  const txns = Array.isArray(record.transactions) ? record.transactions : [];
  return txns.reduce<Record<string, any>>((acc, txn) => {
    const meta = txn?.metadata ?? {};
    Object.entries(meta).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = value;
      }
    });
    return acc;
  }, {});
};

export const getOtherAssetMetadata = (
  record: Record<string, any> | null | undefined,
): Record<string, any> => {
  if (!record) return {};
  return {
    ...pickOtherAssetTxnMetadata(record),
    ...(record.metadata ?? record.meta ?? {}),
  };
};

export const getOtherAssetSecondaryCategory = (
  record: Record<string, any> | null | undefined,
): string => {
  const meta = getOtherAssetMetadata(record);
  return String(meta.secondaryCategory ?? record?.secondaryCategory ?? "").trim();
};

export const getOtherAssetLocation = (
  record: Record<string, any> | null | undefined,
): string => {
  const meta = getOtherAssetMetadata(record);
  const raw = meta.location ?? record?.location ?? record?.place ?? "";
  return String(raw ?? "").trim();
};

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (): string => dayjs().format("DD/MM/YYYY");

const getHoldingSchemeName = (row: Record<string, any>, meta: Record<string, any>): string =>
  String(
    row.schemeName ??
      row.propertyName ??
      meta.propertyName ??
      row.name ??
      "-",
  );

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ?? holding.transaction ?? holding.txns ?? holding.trxn ?? [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toLowerCase().includes("sell");
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const units = toNum(row.units ?? row.quantity ?? row.qty);
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toNum(row.investedAmount ?? row.costValue ?? row.amount);
    const current = toNum(row.currentAmount ?? row.currentValue ?? row.marketValue);
    return (invested !== null && invested > 0) || (current !== null && current > 0);
  });

export const normalizeOtherAssetReportRow = (row: Record<string, any>): Record<string, any> => {
  const meta = getOtherAssetMetadata(row);
  const schemeName = getHoldingSchemeName(row, meta);
  const secondaryCategory = mapSecondaryCategoryToLabel(getOtherAssetSecondaryCategory(row));

  return withTxnMetadataFields({
    ...row,
    schemeName,
    metadata: meta,
    primaryCategory: normalizePrimaryCategoryLabel(
      String(meta.primaryCategory ?? row.primaryCategory ?? row.category ?? ""),
    ),
    type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
    secondaryCategory,
    location: getOtherAssetLocation(row) || "-",
    propertyName: row.propertyName ?? meta.propertyName,
    investedAmount: row.investedAmount ?? meta.purchaseCost ?? row.amount,
    currentAmount: row.currentAmount ?? meta.currentValue ?? row.currentValue,
    gainLoss: row.gainLoss,
    gainLossPercentage: row.gainLossPercentage ?? row.gainLossPerc,
    cagr: row.cagr ?? meta.cagr ?? "-",
    days: row.averageDays ?? row.days ?? meta.averageDays ?? meta.days ?? "-",
    adjustedCostPrice:
      row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
  });
};

const flattenOtherAssetHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const normalizedHolding = normalizeOtherAssetReportRow(h);
    const meta = normalizedHolding.metadata ?? {};
    const schemeName = normalizedHolding.schemeName;
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;

    for (const t of list) {
      const txnMeta = t.metadata ?? t.meta ?? {};
      const isSell = isSellTxn(t);
      const purchaseCost = t.purchaseCost ?? txnMeta.purchaseCost ?? t.amount ?? t.costValue;
      const currentValue = t.currentValue ?? txnMeta.currentValue ?? t.currentAmount ?? "-";
      const unrealised =
        t.unrealisedGainLoss ??
        t.unrealizedGainLoss ??
        t.unrealisedPNL ??
        (isSell ? "-" : toNum(currentValue) != null && toNum(purchaseCost) != null
          ? Number(currentValue) - Number(purchaseCost)
          : "-");
      const realised =
        isSell
          ? (t.realisedGainLoss ??
            t.realizedGainLoss ??
            t.realisedPnl ??
            t.realisedPNL ??
            txnMeta.realisedPnl ??
            "-")
          : "-";

      const stt = t.stt ?? txnMeta.stt;
      const stampDuty = t.stampDuty ?? txnMeta.stampDuty ?? txnMeta.stamp_duty;
      const otherExpenses =
        t.otherExpenses ?? txnMeta.otherExpenses ?? txnMeta.other_expenses;
      const brokerage = t.brokerage ?? txnMeta.brokerage;
      const dpName = t.dpName ?? txnMeta.dpName ?? txnMeta.dp_name ?? meta.dpName;
      const mapped = withTxnMetadataFields({
          ...normalizedHolding,
          ...t,
          metadata: { ...meta, ...txnMeta },
          schemeName: t.schemeName ?? schemeName,
          userId: t.userId ?? h.userId,
          memberName: t.memberName ?? h.memberName,
          txnType: t.txnType ?? t.transType ?? t.transactionType ?? "-",
          purchaseDate: formatEpochDate(
            t.purchaseDate ?? t.transactionDate ?? t.transactiondate ?? t.trxnDate,
          ),
          currentDate: formatEpochDate(t.currentDate ?? txnMeta.currentDate ?? meta.currentDate) || resolveCurrentDateOrToday(),
          amount: t.amount ?? txnMeta.purchaseCost ?? t.costValue,
          currentValue,
          unrealisedGainLoss: unrealised,
          realisedGainLoss: realised,
          stt,
          stampDuty,
          otherExpenses,
          brokerage,
          dpName,
        });
      txns.push({
        ...mapped,
        stt: mapped.stt ?? stt,
        stampDuty: mapped.stampDuty ?? stampDuty,
        otherExpenses: mapped.otherExpenses ?? otherExpenses,
        brokerage: mapped.brokerage ?? brokerage,
        dpName: mapped.dpName || dpName,
      });
    }
  }
  return txns;
};

const extractOtherAssetHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    gainLossPercentage: info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagr: info.cagr ?? info.roi,
  };
};

export type ParsedOtherAssetReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseOtherAssetReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedOtherAssetReport => {
  let rows: any[] = [];
  const holdings = extractOtherAssetHoldings(payload);
  const totalHoldingInfo = extractTotalHoldingInfo(payload);
  const totalPages = Number(payload?.totalPages) || 1;
  const pageIndex = Number(payload?.pageIndex) || 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding":
      rows = filterPositiveUnits(holdings).map(normalizeOtherAssetReportRow);
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    case "Transaction":
      rows = flattenOtherAssetHoldingsTransactions(holdings);
      totalRecords = rows.length;
      break;
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergeOtherAssetTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName}-${r.purchaseDate}-${r.txnType}-${r.amount}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName}-${row.purchaseDate}-${row.txnType}-${row.amount}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
